/**
 * RoutePlanner Component — Plan food trip with optimized route (refined)
 */
import React, { useState } from 'react';
import { Clock, Navigation, Car, Check } from 'lucide-react';
import { Modal, Spinner, ErrorMessage } from '../../shared';
import { routeApi } from '../../../services/api';

export function RoutePlanner({ isOpen, onClose, restaurants, t }) {
  const [selectedIds, setSelectedIds] = useState([]);
  const [startTime, setStartTime] = useState('09:00');
  const [loading, setLoading] = useState(false);
  const [route, setRoute] = useState(null);
  const [error, setError] = useState(null);

  const toggleRestaurant = (id) => {
    setSelectedIds(prev =>
      prev.includes(id)
        ? prev.filter(rid => rid !== id)
        : [...prev, id]
    );
  };

  const selectAll = () => setSelectedIds(restaurants.map(r => r.id));
  const clearSelection = () => setSelectedIds([]);

  const handleGenerate = async () => {
    if (selectedIds.length < 2) {
      setError(new Error('Please select at least 2 restaurants'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await routeApi.planTrip(selectedIds, startTime, { optimizeRoute: true });
      setRoute(response);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const openMaps = () => {
    if (route?.navigation?.googleMapsUrl) {
      window.open(route.navigation.googleMapsUrl, '_blank');
    }
  };

  const handleReset = () => {
    setRoute(null);
    setSelectedIds([]);
    setError(null);
  };

  const getStopColor = (index, total) => {
    if (index === 0) return 'bg-emerald-500';
    if (index === total - 1) return 'bg-red-500';
    return 'bg-primary-500';
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t ? t('planTrip') : 'Food Trip Planner'}
      icon="🗺️"
      size="xl"
    >
      <div className="space-y-4">
        {!route ? (
          <>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-semibold text-stone-700">Select Restaurants</label>
                <div className="flex gap-3">
                  <button
                    onClick={selectAll}
                    className="text-[11px] text-primary-500 hover:text-primary-600 font-semibold transition-colors"
                  >
                    Select All
                  </button>
                  <button
                    onClick={clearSelection}
                    className="text-[11px] text-stone-400 hover:text-stone-600 font-medium transition-colors"
                  >
                    Clear
                  </button>
                </div>
              </div>

              <div className="max-h-40 overflow-y-auto space-y-1 scrollbar-thin">
                {restaurants.map(r => (
                  <label
                    key={r.id}
                    className={`flex items-center gap-2.5 p-2.5 rounded-xl cursor-pointer transition-all duration-200 ${selectedIds.includes(r.id)
                        ? 'bg-primary-50/60 border border-primary-200/60'
                        : 'bg-warm-50 border border-transparent hover:bg-stone-100'
                      }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(r.id)}
                      onChange={() => toggleRestaurant(r.id)}
                      className="rounded text-primary-500 focus:ring-primary-200 border-stone-300"
                    />
                    <span className="flex-1 text-sm font-medium text-stone-700 truncate">{r.name}</span>
                    <span className="text-[11px] text-stone-400 font-medium">{r.category}</span>
                  </label>
                ))}
              </div>

              <p className="text-[11px] text-stone-400 mt-1.5 font-medium">
                {selectedIds.length} selected
              </p>
            </div>

            <div>
              <label className="text-sm font-semibold text-stone-700 mb-1.5 block">Start Time</label>
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="bg-stone-100 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200 text-stone-700 transition-all duration-200"
              />
            </div>

            <button
              onClick={handleGenerate}
              disabled={loading || selectedIds.length < 2}
              className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold disabled:opacity-50 flex items-center justify-center gap-2 transition-all duration-200 active:scale-[0.97]"
            >
              {loading ? (
                <>
                  <Spinner size="sm" className="text-white" />
                  Planning...
                </>
              ) : (
                <>
                  <Navigation size={16} />
                  Generate Route
                </>
              )}
            </button>

            {selectedIds.length < 2 && (
              <p className="text-[11px] text-stone-400 text-center font-medium">
                Select at least 2 restaurants to plan a route
              </p>
            )}
          </>
        ) : (
          <>
            {/* Route Results */}
            <div className="relative pl-4 border-l-2 border-dashed border-stone-200 space-y-3">
              {route.stops?.map((stop, index) => (
                <div key={index} className="relative animate-slideUp" style={{ animationDelay: `${index * 0.05}s` }}>
                  <div
                    className={`absolute -left-[21px] top-1.5 w-2.5 h-2.5 rounded-full ring-2 ring-white ${getStopColor(index, route.stops.length)}`}
                  />
                  <div className="bg-warm-50 rounded-xl p-3 ml-2 border border-stone-100/60">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-[11px] text-stone-400 flex items-center gap-1 font-medium">
                        <Clock size={10} />
                        {stop.timing?.suggestedArrival}
                      </p>
                      <span className="text-[10px] bg-primary-50 text-primary-600 border border-primary-100/60 px-2 py-0.5 rounded-full font-semibold">
                        {stop.timing?.mealType}
                      </span>
                    </div>
                    <p className="font-bold text-sm text-stone-800">{stop.restaurant?.name}</p>
                    <p className="text-[11px] text-stone-400 font-medium">{stop.restaurant?.category}</p>

                    <div className="flex items-center gap-4 mt-2 text-[11px] text-stone-400 font-medium">
                      {stop.distance?.fromPrevious > 0 && (
                        <span className="flex items-center gap-1">
                          <Car size={10} />
                          {stop.distance.fromPrevious} km
                        </span>
                      )}
                      <span>~{stop.timing?.estimatedDiningTime} min</span>
                      <span>~฿{stop.estimatedCost}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Summary */}
            <div className="grid grid-cols-2 gap-2 p-3.5 bg-warm-50 rounded-xl text-[11px] border border-stone-100/60">
              <div>
                <p className="text-stone-400 font-medium uppercase tracking-wider">Distance</p>
                <p className="font-bold text-stone-800 text-sm">{route.summary?.totalDistance} km</p>
              </div>
              <div>
                <p className="text-stone-400 font-medium uppercase tracking-wider">Duration</p>
                <p className="font-bold text-stone-800 text-sm">{route.summary?.totalDuration} hrs</p>
              </div>
              <div>
                <p className="text-stone-400 font-medium uppercase tracking-wider">Est. Cost</p>
                <p className="font-bold text-stone-800 text-sm">฿{route.summary?.estimatedTotalCost}</p>
              </div>
              <div>
                <p className="text-stone-400 font-medium uppercase tracking-wider">End Time</p>
                <p className="font-bold text-stone-800 text-sm">{route.summary?.endTime}</p>
              </div>
            </div>

            {/* Tips */}
            <div className="text-[11px] text-stone-500 space-y-1 font-medium">
              {route.tips?.slice(0, 2).map((tip, i) => (
                <p key={i} className="flex items-start gap-1.5">
                  <span className="text-primary-400 mt-0.5">•</span>
                  {tip}
                </p>
              ))}
            </div>

            {/* Actions */}
            <div className="flex gap-2">
              <button
                onClick={openMaps}
                className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-all duration-200 active:scale-[0.97]"
              >
                <Navigation size={14} />
                {t ? t('startTrip') : 'Start Trip'}
              </button>
              <button
                onClick={handleReset}
                className="px-4 py-2.5 bg-stone-100 hover:bg-stone-200 text-stone-600 rounded-xl text-sm font-medium transition-all duration-200"
              >
                Reset
              </button>
            </div>
          </>
        )}

        {error && <ErrorMessage error={error} onRetry={handleGenerate} />}
      </div>
    </Modal>
  );
}

export default RoutePlanner;
